import {
  AbsoluteCenter,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  Spinner,
  usePrevious,
  useTheme,
} from '@chakra-ui/react';
import React, {useEffect, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useAuth from '../../../hooks/useAuth';
import {Community} from '../../../types/community';
import {isStandalone, isIphone} from '../../../utils/deviceCheck';
import {BlueButton} from '../../button';
import Icon from '../../Icon';
import {ToastMessage} from '../../toast';
import {TextG10, TextG14, TextG18} from '../../typography';
import UserAvatar from '../../UserAvatar';
import ChatComment, {ChatComment as ChatCommentInterface} from '../ChatComment';
import CommentInput from '../ChatComment/CommentInput';
import ChatNotificationsNudge from '../ChatNotificationsNudge';
import CommentContextModal from '../CommentContextModal';
import {ImageFileToURL} from '../../../pages/Profile/Edit/CropImage';
import {MentionsInput, Mention} from 'react-mentions';

const ThreadTab = ({
  name,
  articleId,
  isRSVPd,
  headerHeight,
  fetchCommentsHook,
  fetchUsersHook,
  addComment,
  updateComment,
  deleteComment,
  reportComment,
  helpfulComment,
  showUserRSVP = true,
  mentionUser,
  noMessageComponent = (
    <Flex flexDir="column" gap={8}>
      <Icon iconName="fi-rr-comment" style={{fontSize: '48px', color: '#F4F4F4'}} />
      <TextG14 fontWeight="500">
        Welcome to this exclusive event chat just for attendees!
        <br />
        <br />
        Make the most out of your experience by asking questions, sharing your excitement about the
        event, or planning meet-ups with like-minded locals on Walkabout.
      </TextG14>
    </Flex>
  ),
}: {
  name: string;
  articleId?: number | undefined;
  isRSVPd: boolean;
  community?: Community;
  headerHeight: number;
  fetchCommentsHook: any;
  fetchUsersHook?: any;
  addComment: (content: string, parent_id?: string, imageURLs?: Array<any>) => void;
  updateComment: (commentId: string, content: string, parent_id?: string) => void;
  deleteComment: (commentId: string) => void;
  reportComment: (commentId: string) => void;
  helpfulComment: (commentId: string) => void;
  showUserRSVP?: boolean;
  mentionUser?: {id: number; handle: string};
  noMessageComponent?: JSX.Element;
}) => {
  const theme = useTheme();
  const fetchComments = articleId ? fetchCommentsHook(articleId) : fetchCommentsHook();
  const [eventComments, setEventComments] = useState<Array<ChatCommentInterface> | null>(null);
  const previousComments = usePrevious(eventComments);
  const [newLatestComment, setNewLastestComment] = useState<ChatCommentInterface | null>(null);
  const [latestRef, {rootRef, isVisible, wasEverVisible}] = useTrackVisibility();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [scrollPos, setScrollPos] = useState(0);
  const {user} = useAuth();
  //const addCommunityComment = useCreateCommunityComment();
  const [inputOpen, setInputOpen] = useState(false);
  const [inputHidden, setInputHidden] = useState(false);
  const [replyToComment, setReplyToComment] = useState<ChatCommentInterface | null>(null);
  const [contextModalComment, setContextModalComment] = useState<ChatCommentInterface | null>(null);
  const [quickMessage, setQuickMessage] = useState('');
  const [imageURLs, setImageURLs] = useState<Array<any>>([]);
  const [users, setUsers] = useState<Array<any>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mentionRef = useRef<HTMLElement>(null);
  const prevOpen = usePrevious(inputOpen);
  const prevHidden = usePrevious(inputHidden);

  const shouldShowNotificationNudge = !!!(
    user?.emailEnabled &&
    user.smsEnabled &&
    user.pushEnabled
  );

  const [bottomBrowserOffset, setBottomBrowserOffset] = useState(!isStandalone() ? 83 : 0);

  // Fetch users for mentions
  const fetchUsers = articleId && fetchUsersHook ? fetchUsersHook(articleId) : fetchUsersHook ? fetchUsersHook() : {data: null};
  
  useEffect(() => {
    if (!fetchUsers.data) return;
    const tmpUsers: Array<any> = [];
    if (fetchUsers.data.pages) {
      fetchUsers.data.pages.map((p) =>
        p.data.data.map((user) =>
          tmpUsers.push({id: user.id, display: user.handle, avatar: user.profile_photo_url})
        )
      );
      setUsers(tmpUsers);
    } else if (fetchUsers.data.data?.data) {
      fetchUsers.data.data.data.map((user) =>
        tmpUsers.push({id: user.id, display: user.handle, avatar: user.profile_photo_url})
      );
      setUsers(tmpUsers);
    }
  }, [fetchUsers.data?.pages, fetchUsers.data?.data]);

  // const users = uniqBy(
  //   map(eventComments, (comment) => {
  //     return {
  //       id: comment.user.id,
  //       display: comment.user.handle,
  //       avatar: get(comment.user, 'profile_photo_url'),
  //     };
  //   }),
  //   (c) => c.id
  // );

  const handleAddComment = (content: string, parent_id?: string, imageURLs?: Array<any>) => {
    addComment(content, parent_id, imageURLs);
    setInputOpen(false);
    setReplyToComment(null);
  };

  const handleQuickSend = () => {
    // Require content - cannot send image-only messages
    if (!quickMessage.trim()) return;
    if (!isRSVPd) {
      ToastMessage({
        status: 'error',
        text: 'You must RSVP to this event to participate in chat.',
      });
      return;
    }
    addComment(quickMessage.trim(), undefined, imageURLs.length > 0 ? imageURLs : undefined);
    setQuickMessage('');
    setImageURLs([]);
  };

  const handlePhotoClick = () => {
    if (!isRSVPd) {
      ToastMessage({
        status: 'error',
        text: 'You must RSVP to this event to participate in chat.',
      });
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const MAX_FILES = 4;
    if (files.length > MAX_FILES || files.length + imageURLs.length > MAX_FILES) {
      ToastMessage({
        status: 'error',
        text: `You can only upload ${MAX_FILES} files.`,
      });
      return;
    }

    // Process images and add to state
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const image: any = await ImageFileToURL(file);
      if (!imageURLs.includes(image)) {
        setImageURLs((prev) => [...prev, image]);
      }
    }

    // Clear the input so the same file can be selected again
    e.target.value = '';
  };

  const handleFetchNextPage = () => {
    fetchComments.fetchNextPage();
  };

  const handleReplyClick = (comment: ChatCommentInterface) => {
    setReplyToComment(comment);
    setInputOpen(true);
    // Optionally scroll to bottom to show input
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }, 100);
  };

  const handleScrollToParent = (parentId: string) => {
    // Always open modal for consistent behavior
    const commentWithParent = eventComments?.find((c) => c.parent_id === parentId);

    if (commentWithParent?.parent) {
      // Open the CommentContextModal with the parent comment
      setContextModalComment(commentWithParent.parent);
    } else {
      // Fallback: show toast if we can't find the parent data
      ToastMessage({
        status: 'info',
        text: 'Opening conversation...',
      });
    }
  };

  useEffect(() => {
    if (!isStandalone() && ((prevHidden && !inputHidden) || (!prevOpen && inputOpen))) {
      setBottomBrowserOffset(0);
    }
  }, [inputOpen, inputHidden]);

  // const isRVSPd =
  //   event &&
  //   event.rsvp_status.status_value &&
  //   event.rsvp_status.status_value != EventRSVPStatus.NOT_INTERESTED;

  useEffect(() => {
    if (!fetchComments.data || !fetchComments.data.pages) return;
    const comments: Array<ChatCommentInterface> = [];
    fetchComments.data.pages.map((p: any) =>
      p.data.data.map((comment: any) => comments.push(comment))
    );
    // Backend now returns comments in flat chronological order (including replies)
    setEventComments(comments);
  }, [fetchComments.data?.pages]);

  useEffect(() => {
    if (isVisible) {
      setNewLastestComment(null);
    }
  }, [isVisible]);

  useEffect(() => {
    if (
      previousComments &&
      eventComments &&
      previousComments[0] &&
      eventComments[0] &&
      previousComments[0].id != eventComments[0].id
    ) {
      if (eventComments[0].user?.id == user?.id || scrollPos >= -100) {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      } else {
        setNewLastestComment(eventComments[0]);
      }
    }
  }, [previousComments, eventComments]);

  // Handle pre-populating @mention from navigation state
  useEffect(() => {
    if (mentionUser && mentionUser.handle && mentionUser.id) {
      const mentionText = `@${mentionUser.id}[${mentionUser.handle}] `;
      setQuickMessage(mentionText);
      setInputHidden(false);
      setInputOpen(false);
      // Scroll to input
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 300);
    }
  }, [mentionUser]);

  if (fetchComments.isLoading)
    return (
      <Center h="100vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );

  return (
    <Box overflow="hidden !important">
      {newLatestComment && !inputHidden && !inputOpen && (
        <BlueButton
          onClick={() =>
            bottomRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            })
          }
          style={{
            zIndex: '1400',
            position: 'fixed',
            bottom: '100px',
            width: '33.3%',
            left: '33.3%',
            padding: '0px 5px',
          }}>
          <Flex gap={2} align="center">
            <TextG10>Latest Messages</TextG10>
            <Icon style={{fontSize: '10px'}} iconName="fi-rr-arrow-down" />
          </Flex>
        </BlueButton>
      )}
      {!inputHidden && (
        <Box
          borderTop={`1px solid ${theme.colors.lightGrey}`}
          background="white"
          w="100%"
          position="fixed"
          bottom="0px"
          zIndex="1399"
          left={0}
          px="5%"
          pt={1}
          pb={3}>
          {articleId && shouldShowNotificationNudge && user?.hasEarlyAccess && (
            <ChatNotificationsNudge articleId={articleId} />
          )}
          {!inputOpen ? (
            <>
              <Flex my={3} align="center" gap={2}>
                {/* Photo Icon */}
                <IconButton
                  aria-label="Add photo"
                  icon={<Icon iconName="fi-rr-add-image" style={{fontSize: '28px'}} />}
                  onClick={handlePhotoClick}
                  variant="ghost"
                  color={theme.colors.grey}
                  _hover={{color: theme.colors.blue}}
                  size="md"
                  minW="auto"
                />

                {/* Hidden file input */}
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*;capture=camera"
                  onChange={handleFileSelect}
                  display="none"
                  multiple
                />

                {/* Message Input Field with Mentions Support */}
                <Box flex={1} position="relative">
                  <style>
                    {`
                      .quick-message-input textarea {
                        margin: 0 !important;
                      }
                    `}
                  </style>
                  <MentionsInput
                    inputRef={mentionRef}
                    value={quickMessage}
                    onChange={(e) => {
                      setQuickMessage(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleQuickSend();
                      }
                    }}
                    placeholder="Type a Message to the chat..."
                    className="quick-message-input"
                    style={{
                      control: {
                        backgroundColor: '#fff',
                        fontSize: 14,
                        fontWeight: 'normal',
                        minHeight: '40px',
                        borderRadius: '20px',
                        border: '1px solid rgb(226, 232, 240)',
                      },
                      highlighter: {
                        padding: '10px 16px',
                        border: 'none',
                        overflow: 'hidden',
                        margin: 0,
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 1,
                        color: '#E02F84',
                      },
                      input: {
                        padding: '10px 16px',
                        outline: 'none',
                        border: 'none',
                        margin: 0,
                        boxSizing: 'border-box',
                      },
                      suggestions: {
                        zIndex: 9999,
                        list: {
                          backgroundColor: 'white',
                          border: '1px solid rgba(0,0,0,0.15)',
                          fontSize: 14,
                          maxHeight: '20vh',
                          overflowY: 'auto',
                          borderRadius: '6px',
                        },
                        item: {
                          padding: '5px 15px',
                          borderBottom: '1px solid rgba(0,0,0,0.15)',
                          '&focused': {
                            backgroundColor: '#cee4e5',
                          },
                        },
                      },
                    }}>
                    <Mention
                      trigger="@"
                      data={users}
                      displayTransform={(id, display) => `@${display}`}
                      markup="@__id__[__display__]"
                      renderSuggestion={(suggestion: any, search, highlightedDisplay) => (
                        <Flex align="center" gap={2}>
                          <UserAvatar
                            user={{profile_photo_url: suggestion.avatar} as any}
                            size="sm"
                            w="30px"
                            h="30px"
                          />
                          <Box>{suggestion.display}</Box>
                        </Flex>
                      )}
                      style={{
                        backgroundColor: '#cee4e5',
                        margin: '0',
                        padding: '0',
                      }}
                    />
                  </MentionsInput>
                </Box>

                {/* Send Icon */}
                <IconButton
                  minW="31px"
                  minH="28px"
                  aria-label="Send message"
                  icon={<Icon iconName="fi-rr-paper-plane" style={{fontSize: '28px'}} />}
                  onClick={handleQuickSend}
                  isDisabled={!quickMessage.trim()}
                  variant="ghost"
                  color={
                    quickMessage.trim()
                      ? theme.colors.blue
                      : theme.colors.grey
                  }
                  _hover={{
                    color:
                      quickMessage.trim()
                        ? theme.colors.darkBlue
                        : theme.colors.grey,
                  }}
                  _disabled={{
                    opacity: 0.4,
                    cursor: 'not-allowed',
                  }}
                />
              </Flex>

              {/* Image Previews */}
              {imageURLs.length > 0 && (
                <HStack mb={2} spacing={2} overflowX="auto">
                  {imageURLs.map((url, index) => (
                    <Box key={index} position="relative">
                      <Image
                        src={url}
                        alt={`Preview ${index + 1}`}
                        height="60px"
                        width="60px"
                        objectFit="cover"
                        borderRadius="8px"
                      />
                      <IconButton
                        aria-label="Remove image"
                        icon={<Icon iconName="fi-rr-cross" style={{fontSize: '10px'}} />}
                        onClick={() => {
                          setImageURLs((prev) => prev.filter((_, i) => i !== index));
                        }}
                        position="absolute"
                        top="-8px"
                        right="-8px"
                        size="xs"
                        borderRadius="full"
                        bg="white"
                        color="black"
                        minW="20px"
                        w="20px"
                        h="20px"
                        _hover={{bg: 'gray.100'}}
                      />
                    </Box>
                  ))}
                </HStack>
              )}
            </>
          ) : (
            <Box my={3}>
              <CommentInput
                fetchUsersHook={fetchUsersHook}
                articleId={articleId}
                onCloseClick={() => {
                  setInputOpen(false);
                  setReplyToComment(null);
                  setQuickMessage(''); // Clear quick message when closing
                }}
                onSaveClick={(content, parent_id, imageURLs) => {
                  handleAddComment(content, parent_id, imageURLs);
                  setQuickMessage(''); // Clear quick message after sending
                }}
                parentComment={replyToComment || undefined}
                defaultContent={quickMessage} // Pass quickMessage as defaultContent
              />
            </Box>
          )}
        </Box>
      )}

      {!eventComments ? (
        <Center
          h={`calc(100vh - ${inputHidden ? '0px' : '75px'} - ${headerHeight}px)`}
          mt={`${headerHeight}px`}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor={theme.colors.lightGrey}
            color={theme.colors.lightBlue}
            size="lg"
          />
        </Center>
      ) : eventComments.length > 0 || inputOpen ? (
        <Flex
          overscrollBehavior="none !important"
          flexDir="column-reverse"
          id="scrollableDiv"
          overflowY="auto"
          height={`calc(100vh - ${inputHidden ? '0px' : '75px'} - ${headerHeight}px  - 10px)`}
          mt={`${headerHeight}px`}
          px={4}
          //pb={inputOpen ? '83px' : 0}
        >
          <InfiniteScroll
            onScroll={(e) => {
              const scrollTop = (e.target as HTMLDivElement).scrollTop || 0;
              setScrollPos(scrollTop);
            }}
            scrollThreshold={'500px'}
            scrollableTarget="scrollableDiv"
            dataLength={eventComments.length}
            next={handleFetchNextPage}
            style={{
              display: 'flex',
              flexDirection: 'column-reverse',
              paddingBottom: inputOpen && !(isStandalone() && isIphone) ? '30%' : 0,
            }} //To put endMessage and loader to the top.
            inverse={true}
            hasMore={fetchComments?.hasNextPage || false}
            loader={
              <Center py="30px">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor={theme.colors.lightGrey}
                  color={theme.colors.lightBlue}
                  size="lg"
                />
              </Center>
            }>
            <Box h="1px" ref={bottomRef} />
            {eventComments.map((comment, i) => {
              // Since messages are in reverse order (newest first), we need to invert the logic
              const prevComment = i > 0 ? eventComments[i - 1] : null;
              const nextComment = i < eventComments.length - 1 ? eventComments[i + 1] : null;

              // In reverse order: "next" is actually older, "prev" is actually newer
              const isGroupStart = !nextComment || nextComment.user_id !== comment.user_id;
              const isGroupEnd = !prevComment || prevComment.user_id !== comment.user_id;

              // Parent comment data is now included directly from backend
              const parentComment =
                comment.parent_id && comment.parent ? comment.parent : undefined;

              return (
                <Box
                  key={comment.id}
                  ref={comment.id == newLatestComment?.id ? latestRef : null}
                  data-comment-id={comment.id}>
                  <ChatComment
                    fetchUsersHook={fetchUsersHook}
                    addComment={addComment}
                    updateComment={updateComment}
                    deleteComment={deleteComment}
                    reportComment={reportComment}
                    helpfulComment={helpfulComment}
                    comment={comment}
                    articleId={articleId}
                    name={name}
                    isRSVPd={isRSVPd}
                    setInputOpen={setInputOpen}
                    setInputHidden={setInputHidden}
                    showUserRSVP={showUserRSVP}
                    onReplyClick={handleReplyClick}
                    onScrollToParent={handleScrollToParent}
                    isGroupStart={isGroupStart}
                    isGroupEnd={isGroupEnd}
                    parentComment={parentComment}
                  />
                </Box>
              );
            })}
          </InfiniteScroll>
        </Flex>
      ) : (
        <AbsoluteCenter
          px={4}
          style={{position: 'fixed'}}
          w="100%"
          textAlign="center"
          color={theme.colors.grey}>
          {noMessageComponent}
        </AbsoluteCenter>
      )}

      {/* CommentContextModal for viewing parent comments that aren't loaded */}
      <CommentContextModal
        comment={contextModalComment}
        onClose={() => setContextModalComment(null)}
        eventId={articleId}
        addComment={addComment}
        updateComment={updateComment}
        deleteComment={deleteComment}
        reportComment={reportComment}
        helpfulComment={helpfulComment}
        fetchUsersHook={fetchUsersHook}
        fetchCommentsHook={fetchCommentsHook}
      />
    </Box>
  );
};

export default ThreadTab;
